// MIDAS V3 — Theme switcher (Phase 1 MVP + Phase 6 polish)
//
// A 24x24 mono "C / M" toggle for swapping between the two v3 palettes
// (`p-claude` ↔ `p-machineid`). Persistence is handled by the slice
// reducer — see midasV3Slice.ts → setMidasV3Theme.
//
// Layout target: sits in the rail brand row, between the `v3` chip and
// the chevron toggle. On the collapsed rail this component is omitted
// (the rail switches palette globally via the layout root class).
//
// Phase 6 polish:
//   • Tooltip via Radix `Tooltip` from v2/ui/tooltip (already used elsewhere
//     in v3, e.g. MessageActionsV3). Falls back gracefully via the native
//     `title` attribute when the Radix provider isn't mounted.
//   • 250ms transition on background + color when the palette swaps. The
//     `--midas-v3-*` custom properties themselves can't be transitioned
//     (browsers don't animate var() changes for native discrete properties),
//     but the resolved `background` / `color` style values DO animate, so a
//     longer transition smooths the visual handoff.
//   • Reduced-motion override: the token sheet's global rule
//     (`transition-duration: 0.001ms`) already nukes this for users who
//     prefer reduced motion. No extra JS guard needed.
//
// Spec: MIDAS_V3_PHASE1_SCAFFOLD_SPEC.md §13 + MIDAS_V3_PHASE6 §6
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useCallback } from "react";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../shared/hooks/sharedHooks";
import { setMidasV3Theme } from "../../midasV3Slice";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../v2/ui/tooltip";

export interface MidasThemeSwitcherV3Props {
  /** Optional tooltip — usually localised by the caller. */
  title?: string;
}

const MidasThemeSwitcherV3: React.FC<MidasThemeSwitcherV3Props> = ({ title }) => {
  const dispatch = useAppDispatch();
  const theme = useAppSelector((s) => s.midasV3.theme);

  const onClick = useCallback(() => {
    dispatch(setMidasV3Theme(theme === "claude" ? "machineid" : "claude"));
  }, [dispatch, theme]);

  const label = theme === "claude" ? "C" : "M";
  const tooltip =
    title ||
    (theme === "claude"
      ? "Switch to MachineID palette"
      : "Switch to Claude palette");

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          // Keep the native `title` as a no-Tooltip fallback. Radix sets
          // aria-describedby when active, so screen readers don't read both.
          title={tooltip}
          aria-label={tooltip}
          aria-pressed={theme === "machineid"}
          style={{
            width: 24,
            height: 24,
            padding: 0,
            borderRadius: 6,
            background: "transparent",
            border: "1px solid var(--midas-v3-border)",
            color: "var(--midas-v3-muted)",
            cursor: "pointer",
            display: "grid",
            placeItems: "center",
            fontFamily:
              "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 10.5,
            fontWeight: 600,
            letterSpacing: "0.04em",
            // 250ms — longer than the 120ms hover transitions elsewhere so
            // the palette swap reads as a deliberate "settle". The reduced-
            // motion override in midas-v3-tokens.scss reduces all transitions
            // to 0.001ms when users prefer reduced motion.
            transition:
              "background-color 250ms ease, color 250ms ease, border-color 250ms ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--midas-v3-card-2)";
            e.currentTarget.style.color = "var(--midas-v3-ink)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "var(--midas-v3-muted)";
          }}
        >
          {label}
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <span
          style={{
            fontFamily:
              "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 11,
          }}
        >
          {tooltip}
        </span>
      </TooltipContent>
    </Tooltip>
  );
};

export default MidasThemeSwitcherV3;
